import { DollarSign } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const EB5InvestorVisa = () => {
  const faqs = [
    {
      question: "How much do I need to invest?",
      answer: "The standard EB-5 investment amount is $1,050,000. However, investments in Targeted Employment Areas (TEAs) or rural areas require only $800,000. These amounts include inflation adjustments and may change over time.",
    },
    {
      question: "How long does the EB-5 process take?",
      answer: "The EB-5 process typically takes 2-4 years from initial filing to receiving a green card. This includes I-526 petition approval (12-18 months), visa availability or adjustment of status, and conditional green card receipt. After 2 years, you can remove conditions.",
    },
  ];

  const mainContent = (
    <>
      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-bold mb-6">Secure Your U.S. Green Card Through Investment</h2>
        <p className="text-xl text-muted-foreground mb-8">
          Are you a foreign investor seeking permanent residency in the United States? The EB-5 Immigrant Investor Program offers a direct path to a green card through qualified investment. At Daniel Albert Law Firm, we provide trusted, strategic guidance for high-net-worth individuals and their families.
        </p>

        <h3 className="text-2xl font-bold mb-4">EB-5 Program Overview</h3>
        <p className="mb-6">
          The EB-5 visa program allows foreign nationals to obtain U.S. permanent residency by investing in a new commercial enterprise that creates or preserves at least 10 full-time jobs for qualified U.S. workers.
        </p>

        <h3 className="text-2xl font-bold mb-4">Investment Options</h3>
        <ul className="list-disc pl-6 mb-6">
          <li><strong>Direct Investment:</strong> Invest in and manage your own business</li>
          <li><strong>Regional Center:</strong> Invest in an approved EB-5 regional center project</li>
          <li><strong>Rural Areas:</strong> Lower investment amount with set-aside visas</li>
          <li><strong>Infrastructure Projects:</strong> Priority processing for qualifying projects</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">Benefits of EB-5</h3>
        <ul className="list-disc pl-6 mb-6">
          <li>Permanent residence for you, spouse, and unmarried children under 21</li>
          <li>Live, work, and study anywhere in the U.S.</li>
          <li>No employer sponsorship required</li>
          <li>Path to U.S. citizenship after 5 years</li>
          <li>Access to U.S. education and healthcare systems</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">Our EB-5 Services</h3>
        <ul className="list-disc pl-6 mb-8">
          <li>Investment project evaluation and due diligence</li>
          <li>Business plan preparation</li>
          <li>Form I-526 petition filing</li>
          <li>Source of funds documentation</li>
          <li>Adjustment of status or consular processing</li>
          <li>Form I-829 petition to remove conditions</li>
        </ul>

        <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );

  return (
    <GenericPracticePage
      title="EB-5 Immigrant Investor Visa"
      subtitle="Investment-based green card for foreign entrepreneurs and investors"
      icon={<DollarSign className="h-20 w-20 mx-auto mb-6 text-accent" />}
      mainContent={mainContent}
    />
  );
};

export default EB5InvestorVisa;
